<template>
  <div class="flex flex-wrap gap-x-2 gap-y-3 items-center">
    <!-- Age Brackets Tag -->
    <span v-if="ageVerified" class="px-2 py-1 text-almostBlack bg-pink-300 rounded-md">
      {{ maxAge === 122 ? `${minAge}+` : `${minAge} - ${maxAge} Yrs` }}
    </span>

    <!-- First 8 Support Need Tags -->
    <span
      v-for="(name, i) in firstEightTags"
      :key="i"
      class="px-2 py-1 text-almostBlack bg-calmBlue-300 rounded-md"
    >
      {{ name }}
    </span>

    <!-- Remaining Support Need Tags -->
    <span
      v-for="(name, i) in remainingTags"
      v-show="showingAll && remainingTags.length"
      :key="i"
      class="px-2 py-1 text-almostBlack bg-calmBlue-300 rounded-md"
    >
      {{ name }}
    </span>

    <!-- Show More Button -->
    <UButton
      v-if="remainingTags.length"
      :label="showingAll ? 'Show less' : `Show all (${remainingTags.length})`"
      color="white"
      variant="link"
      :icon="showingAll ? 'mdi:triangle' : 'mdi:triangle-down'"
      size="xl"
      trailing
      :ui="{ gap: { xl: 'gap-x-3' }, icon: { size: { xl: 'size-2.5' } } }"
      @click="showingAll = !showingAll"
    />
  </div>
</template>
<script setup lang="ts">
import type { PublicService } from '~/types/PublicService'

const props = defineProps({
  minAge: {
    type: Number,
    required: true
  },
  maxAge: {
    type: Number,
    required: true
  },
  supportNeedCategoryIds: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => []
  },
  ageVerified: Boolean,
  service: {
    type: Object as PropType<PublicService>,
    required: true
  }
})

// Get support need store for front-end
const frontendSupportNeedCategorySearchStore = useSupportNeedCategoryStore()
const { supportNeedCategories } = storeToRefs(frontendSupportNeedCategorySearchStore)

const { selectedSupportNeedCategories } = useSelectedSupportNeedCategories()
const { selectedPrefs } = useSelectedPreferences()
const { selectedDeliveryFormats } = useSelectedDeliveryFormats()

// If support need categories haven't already been fetched in store load them up
onMounted(() => {
  if (!supportNeedCategories.value.length) {
    frontendSupportNeedCategorySearchStore.fetchSupportNeedCategories()
  }
})

const showingAll = ref(false)

const orderedTags = computed(() => {
  // remove any support needs from selectedSupportNeedCategories that DON'T exist in the service's sncs (props.supportNeedCategoryIds)
  const selectedSupportNeedsThatExistInService = selectedSupportNeedCategories.value.filter((snc) =>
    props.supportNeedCategoryIds.includes(snc.key.split(':')[1])
  )

  // priority tags are the SELECTED support need categories AND the SELECTED preferences
  const selectedSupportNeeds = selectedSupportNeedsThatExistInService.map((snc) => {
    const supportNeed = supportNeedCategories.value.find(
      (category) => `support-needs:${category.uid}` === snc.key
    )
    return supportNeed?.displayText
  })

  const service = props.service

  const selectedPreferencesThatExistInService = selectedPrefs.value.filter((pref) => {
    // the selected preference filter id
    const { serviceFilterKey, filterId } = pref
    // does the service have this filter?
    if (typeof serviceFilterKey === 'string') {
      return service[serviceFilterKey]
    } else if (typeof serviceFilterKey === 'function') {
      return serviceFilterKey(service)
    } else {
      console.error('Invalid serviceFilterKey', { serviceFilterKey, filterId })
      return false
    }
  })

  const selectedPreferences = selectedPreferencesThatExistInService.map((pref) => pref.label)
  const selectedSupportNeedsAndPreferencesInAlphabeticalOrder = [
    ...selectedSupportNeeds,
    ...selectedPreferences
  ].sort()

  const formats = selectedDeliveryFormats.value.map((deliveryFormat) => deliveryFormat.label)

  // add remaining support need categories
  const remainingSupportNeedCategories = props.supportNeedCategoryIds
    .filter(
      (uid) => !selectedSupportNeedCategories.value.map((snc) => snc.key).includes(`support-needs:${uid}`)
    )
    .map((uid) => {
      const supportNeed = supportNeedCategories.value.find((category) => category.uid === uid)
      return supportNeed?.displayText
    })
    .sort()

  // sorted in the following order:
  // 1) selected support needs and selected preferences in alphabetical order
  // 2) selected delivery formats
  // 3) remaining support needs added to the service
  return [
    ...selectedSupportNeedsAndPreferencesInAlphabeticalOrder,
    ...formats,
    ...remainingSupportNeedCategories
  ].filter((tag): tag is string => Boolean(tag))
})

const firstEightTags = computed(() => {
  // if there are 8 or fewer tags, return all of them
  if (orderedTags.value.length <= 8) {
    return orderedTags.value
  } else {
    // if there are more than 8 tags, return the first 8
    return orderedTags.value.slice(0, 8)
  }
})

const remainingTags = computed(() => {
  // if there are 8 or fewer tags, return an empty array
  if (orderedTags.value.length <= 8) {
    return []
  } else {
    // if there are more than 8 tags, return the remaining tags
    return orderedTags.value.slice(8)
  }
})
</script>
